import axios from 'axios';

import useAuthStore from '@entities/auth/model/useAuthStore';
import useAlertStore from '@entities/ui/model/useAlertStore';
import useRequestContextStore from '@entities/ui/model/useRequestContextStore';
import { isMobileApp } from '@shared/utils/fcmToken';

/**
 * [이슈 2024.12] API 응답 지연 및 토큰 만료 오류 수정
 *
 * 문제 1: timeout이 5초로 짧아 느린 API(4초+)에서 데이터가 표시되지 않음
 * 해결: timeout을 30초로 증가하여 DB 쿼리가 느린 API도 처리 가능하도록 함
 *
 * 문제 2: 동시 401 요청 시 첫 번째 요청만 재시도되고 나머지는 실패 처리됨
 * 해결: 대기 큐(failedQueue)를 도입하여 모든 실패 요청을 수집하고,
 *       토큰 갱신 완료 후 대기 중인 모든 요청을 재시도하도록 변경
 *
 * 문제 3: 경쟁 조건으로 중복 clearAuth/redirect 발생 가능
 * 해결: isRedirecting 플래그를 먼저 설정하고 로직 실행하도록 순서 변경
 */
const apiClient = axios.create({
  baseURL: '/api', // setupProxy에서 /api를 인터셉트하여 백엔드로 프록시
  headers: { 'Content-Type': 'application/json' },
  /**
   * [수정] timeout: 5000 → 30000
   * 이유: DB 쿼리가 4초 이상 걸리는 API에서 timeout 발생하여 데이터 미표시
   * 30초로 늘려 느린 쿼리도 안정적으로 처리
   */
  timeout: 30000,
  // Refresh Token을 HttpOnly 쿠키로 쓰기 때문에, 환경에 따라 쿠키 전송이 필요합니다.
  // (CORS + allowCredentials=true 조합에서도 동작)
  withCredentials: true,
});

/**
 * 요청 인터셉터
 * - 요청 config에 `withAuth: true`가 설정된 경우만 헤더 추가
 * - Zustand store에서 인증 정보 가져오기
 * - Alert가 열려있으면 API 요청 차단
 */
apiClient.interceptors.request.use(
  (config) => {
    // Alert가 열려있으면 요청 차단
    const { isOpen } = useAlertStore.getState();
    if (isOpen) {
      // CancelToken을 사용하여 요청 취소
      const cancelTokenSource = axios.CancelToken.source();
      config.cancelToken = cancelTokenSource.token;
      cancelTokenSource.cancel('[apiClient] Alert가 열려있어 요청이 취소되었습니다.');
    }

    config.headers['X-App-User-Agent'] = navigator.userAgent;

    // 앱/모바일이면 서버에 명시적으로 알려서 PC와 세션 격리
    if (typeof window !== 'undefined' && isMobileApp()) {
      config.headers['X-Device-Type'] = 'MOBILE';
    }

    // 백엔드 로그 맥락: 어느 화면에서 어떤 동작인지 MDC에 표시
    const { screen, requestAction } = useRequestContextStore.getState();
    if (screen) {
      config.headers['X-Screen'] = screen;
    }
    if (requestAction) {
      config.headers['X-Request-Action'] = requestAction;
    }

    if (config.withAuth) {
      // Zustand store에서 인증 정보 가져오기
      const state = useAuthStore.getState();
      const { accessToken, token } = state;

      // JWT 토큰은 Authorization 헤더에 Bearer 형식으로 전송
      // enumber, companyCode는 JWT 토큰에서 백엔드가 추출함
      const tokenToUse = accessToken || token;
      if (tokenToUse) {
        config.headers.Authorization = `Bearer ${tokenToUse}`;
      } else {
        console.warn('[apiClient] 인증 토큰 누락');
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * 응답 인터셉터
 * - 401 Unauthorized 시 인증 정보 초기화 및 로그인 페이지로 리다이렉트
 * - 자동 토큰 갱신 없음: 사용자가 헤더 "연장" 버튼을 눌렀을 때만 갱신
 * - 타임아웃 에러는 인증 만료로 처리하지 않음
 */
let isRedirecting = false; // 중복 리다이렉트 방지 플래그

// 자동 토큰 갱신 비활성화: 401 시 refresh 재시도 없이 로그아웃/리다이렉트만 수행 (사용자 헤더 "연장"으로만 갱신)

apiClient.interceptors.response.use(
  (response) => response, // 정상 응답은 그대로 반환
  async (error) => {
    // 타임아웃 에러는 인증 만료로 처리하지 않음
    const isTimeoutError =
      error.code === 'ECONNABORTED' ||
      error.message?.includes('timeout') ||
      error.message?.includes('Timeout');

    // 네트워크 에러나 응답이 없는 경우는 그대로 반환 (타임아웃 포함)
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const originalRequest = error.config;

    // 401 Unauthorized 응답 처리 (타임아웃 에러가 아닌 경우만)
    if (status === 401 && !isTimeoutError) {
      const errorCode = data?.error_code;

      // 인증 관련 에러 코드 처리
      // AUTH_001: 인증 토큰이 필요합니다
      // AUTH_002: 유효하지 않은 토큰입니다
      // AUTH_003: 토큰이 만료되었습니다
      // AUTH_004: 동시접속으로 인한 토큰 무효화 → refresh 시도하면 새 AT 받아서 이 창이 다시 로그인됨. 반드시 refresh 금지.
      // AUTH_005: 블랙리스트 토큰
      // AUTH_006: 유효하지 않은 갱신 토큰입니다
      // AUTH_010: 어드민 비밀번호 변경 등 세션 무효화
      // AUTH_012: 동시접속 정책 변경(어드민)
      const isAuthError = errorCode?.startsWith('AUTH_');
      const isConcurrentLogin = errorCode === 'AUTH_004' || errorCode === 'AUTH_012';

      if (isAuthError) {
        /**
         * [수정] 경쟁 조건 방지를 위한 순서 변경
         *
         * 기존 문제:
         * - 여러 API가 거의 동시에 실패하면 isRedirecting=true 설정 전에
         *   다른 요청도 이 블록에 진입하여 중복 clearAuth/redirect 발생
         *
         * 해결:
         * - 조건 검사와 플래그 설정을 동기적으로 처리하여 경쟁 조건 최소화
         */
        if (!isRedirecting && window.location.pathname !== '/login') {
          // 플래그를 먼저 설정하여 경쟁 조건 방지
          isRedirecting = true;

          // 동시접속으로 튕겨난 경우, 빈 로그인 화면에서 무한루프(자동로그인) 방지
          if (isConcurrentLogin) {
            sessionStorage.setItem('preventAutoLogin', 'true');
          }

          // 인증 정보 초기화
          useAuthStore.getState().clearAuth();

          // Alert 컴포넌트로 알림 표시
          // AUTH_004(동시접속) 시 백엔드 메시지 그대로 표시: "다른 기기에서 로그인되어 현재 세션이 종료되었습니다."
          // AUTH_002 또는 "유효하지 않은 토큰입니다." → 사용자용 문구로 치환 (백엔드 문구는 사용자 노출용이 아님)
          // 그 외 401은 백엔드 message/error_msg 사용, 없으면 기본 메시지
          const backendMessage = data?.message || data?.error_msg;
          const isInvalidTokenMessage =
            errorCode === 'AUTH_002' ||
            (typeof backendMessage === 'string' &&
              backendMessage.trim().replace(/\.$/, '') === '유효하지 않은 토큰입니다');
          const userFriendlyExpiredMessage =
            '로그인 인증 정보가 만료되었습니다.\n다시 로그인해 주세요.';
          const alertMessage =
            errorCode === 'AUTH_004' || errorCode === 'AUTH_012'
              ? backendMessage ||
                (errorCode === 'AUTH_012'
                  ? '동시접속 정책이 변경되어 세션이 종료되었습니다. 다시 로그인해 주세요.'
                  : '다른 기기에서 로그인되어 현재 세션이 종료되었습니다.')
              : isInvalidTokenMessage
                ? userFriendlyExpiredMessage
                : backendMessage || '로그인이 만료되어 자동으로 로그아웃 되었습니다.';

          // 리다이렉트 콜백 함수 정의
          const redirectToLogin = function () {
            isRedirecting = false; // 리다이렉트 완료 후 플래그 초기화

            const currentPath = window.location.pathname;

            // 관리자 영역에서 발생한 인증 만료라면
            if (currentPath.startsWith('/admin')) {
              window.location.href = '/admin/login';
              return;
            }

            window.location.href = '/login';
          };

          // Alert 표시 (확인 버튼 클릭 시 리다이렉트)
          // 인증 관련 Alert이므로 오버레이 클릭으로 닫기 방지
          useAlertStore.getState().showAlert(alertMessage, redirectToLogin, true);
        }
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
